import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { LayoutChangeEvent } from 'react-native';

import {
  AtomicHeaderType,
  type IScreensComponent,
  type ScreensReadModel,
  type SkeletonColumnType,
} from '../../api';
import {
  getScreenComponent,
  convertScreenComponent,
} from '../../api/screensService/getScreenComponent';
import { Flex, List, type ListRef, useSafeArea } from '@banhala/ui-kit';
import { delay, isNil, isNotNil, isWeb } from '../../utils';
import Animated, {
  type SharedValue,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollToTopButton } from './ScrollToTopButton';
import {
  useLikeMap,
  useOutfitBookmarkMap,
  useAddLikeGoods,
  useOnForeground,
  useLogAnalyticsEvent,
} from '../../hooks';
import {
  AtomicComponent,
  HeaderComponent,
  FooterComponent,
} from './utils/createAtomicComponent';
import { ThreeColGoodsRow } from './ItemList/ThreeColGoodsList/ThreeColGoodsRow';
import { TwoColGoodsRow } from './ItemList/TwoColGoodsList/TwoColGoodsRow';
import { GoodsCardSkeletonList } from '@banhala/ui-kit';
import type { RowItem } from './types';
import {
  useFlattenedItems,
  FLATTEN_ITEM_TYPES,
  type FlattenedItem,
} from './useFlattenedItems';
import { useAtomicGoodsViewability } from './useAtomicGoodsViewability';
import { bridge, useNavigator } from '../../utils';
import { useRemoveLikeGoods } from '../../queries';
import { AdAction } from '../../utils/eventLogger/adLogger/types';
import { AtomicScreenProvider } from './context/AtomicScreenContext';
import { useGoodsCardFeedbackModal } from '../GoodsCardFeedback';
import { useLogClickMeatballs } from '../GoodsCardFeedback/hooks';
import { useSetAtom } from 'jotai';
import { feedbackCacheAtom } from './state/feedbackCache.atom';
import { useInstanceId } from '../../hooks';
import { useOnReturnInject } from './hooks/useOnReturnInject';

export interface AtomicRendererProps {
  screen?: string;
  logDisabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 외부 ListRef<IScreensComponent>와 내부 List<FlattenedItem> 간 제네릭 불일치로 any 불가피
  listRef: RefObject<ListRef<any> | null>;
  data: ScreensReadModel;
  fetchNextPage: VoidFunction;
  filterListIndex?: number;
  scrollToFilterList: VoidFunction;
  isFetching: boolean;
  /** scrollToFilterList의 보류 상태 (true→false 전이가 scroll dispatch 시점). */
  isPendingScroll?: boolean;
  refetch: VoidFunction;
  hideScrollToTop?: boolean;
  onScrollY?: (scrollY: number) => void;
  scrollYSharedValue?: SharedValue<number>;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  /**
   * 스크롤 종료(onMomentumEnd / onScrollEndDrag) 시점에 UI 쓰레드(worklet 컨텍스트)에서
   * 직접 호출되는 핸들러. 매 프레임이 아닌 종료 시점에만 실행되므로 Android 등 일부 환경에서
   * 매 프레임 처리 시 발생하는 jank 없이 헤더 토글 등 종료 시점 처리에 적합.
   */
  onScrollEndWorklet?: (scrollY: number) => void;
  itemHeightsRef?: RefObject<{ [key: number]: number }>;
  onItemHeightChange?: (index: number) => void;
  filterParams?: Record<string, unknown>;
  /**
   * 로드모어 스켈레톤 열 타입. iOS/Android와 1:1 대칭으로 meta API 응답에서 파생.
   * 미지정 시 `'twoCol'` fallback (서버 default 및 iOS/Android 기본값과 동일).
   */
  skeletonType?: SkeletonColumnType;
}

const AnimatedList = Animated.createAnimatedComponent(List<FlattenedItem>);

/**
 * 서버에서 받은 Atomic 컴포넌트 배열을 렌더링하는 메인 리스트.
 *
 * 2col/3col GoodsList/CardList는 내부에 수직 List를 가져서
 * 수직×수직 중첩이 발생하므로, 행 단위로 평탄화하여 중첩을 제거한다.
 * 나머지 컴포넌트(Header, FilterList, Divider 등)는 원본 그대로 렌더링한다.
 */
export const AtomicRenderer = ({
  listRef,
  data,
  fetchNextPage,
  filterListIndex,
  scrollToFilterList,
  isFetching,
  isPendingScroll,
  refetch,
  screen,
  hideScrollToTop,
  onScrollY,
  scrollYSharedValue,
  onBeginDrag,
  onEndDrag,
  onScrollEndWorklet,
  itemHeightsRef,
  onItemHeightChange,
  logDisabled,
  filterParams,
  skeletonType,
}: AtomicRendererProps) => {
  const atomicRendererScrollY = useSharedValue(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevContentSizeHeightRef = useRef<number>(0);
  const layoutRef = useRef<{ height: number }>({ height: 0 });
  const isFetchingNextPageRef = useRef(false);

  const internalItemHeightsRef = useRef<{ [key: number]: number }>({});
  const itemHeights = itemHeightsRef ?? internalItemHeightsRef;
  const { likeMap, setLikeMap } = useLikeMap();
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({
    initialMap: {},
  });
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const [componentOverrides, setComponentOverrides] = useState<
    Record<number, IScreensComponent>
  >({});

  const debouncedFetchNextPage = useCallback(() => {
    if (isFetchingNextPageRef.current) return;
    isFetchingNextPageRef.current = true;
    fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    if (!isFetching) {
      isFetchingNextPageRef.current = false;
    }
  }, [isFetching]);

  const { injected, fetchAndInject, resetInjected, splitComponentByRow, consumePendingEvent } =
    useOnReturnInject();

  // 새 데이터 로드 시 초기화 (pagination은 제외)
  const prevFirstComponentIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const firstId = data.components[0]?.componentId;
    if (firstId !== undefined && firstId === prevFirstComponentIdRef.current) {
      return;
    }
    prevFirstComponentIdRef.current = firstId;
    setComponentOverrides({});
    resetInjected();
  }, [data.components, resetInjected]);

  // 복귀 시 inject VIEW 이벤트 발화 (iOS trackInjectLoggingEvent 대응)
  useOnForeground(
    useCallback(() => {
      const event = consumePendingEvent();
      if (!event) return;
      bridge.logAnalyticsEvent({
        name: event.eventName,
        ...event.eventLogging,
      });
    }, [consumePendingEvent]),
  );

  const { effectiveComponents, originalIndices } = useMemo(() => {
    let components = data.components.map(
      c => componentOverrides[c.componentId] ?? c,
    );
    let indices = data.components.map((_, i) => i);

    if (injected && injected.afterIndex < components.length) {
      const targetComponent = components[injected.afterIndex];
      if (targetComponent) {
        const itemList = targetComponent.entity.itemList;
        const realItemIndex = itemList
          ? itemList.findIndex(
              (e: { item?: { sno?: number } }) =>
                e.item?.sno === injected.itemSno,
            )
          : -1;
        const split = splitComponentByRow(
          targetComponent,
          realItemIndex >= 0 ? realItemIndex : 0,
        );

        if (split) {
          components = [
            ...components.slice(0, injected.afterIndex),
            split.top,
            ...injected.components,
            split.bottom,
            ...components.slice(injected.afterIndex + 1),
          ];
          indices = [
            ...indices.slice(0, injected.afterIndex),
            injected.afterIndex,
            ...injected.components.map(() => -1),
            injected.afterIndex,
            ...indices.slice(injected.afterIndex + 1),
          ];
        } else {
          components = [
            ...components.slice(0, injected.afterIndex + 1),
            ...injected.components,
            ...components.slice(injected.afterIndex + 1),
          ];
          indices = [
            ...indices.slice(0, injected.afterIndex + 1),
            ...injected.components.map(() => -1),
            ...indices.slice(injected.afterIndex + 1),
          ];
        }
      }
    }

    return { effectiveComponents: components, originalIndices: indices };
  }, [data.components, componentOverrides, injected, splitComponentByRow]);

  const flattenedItems = useFlattenedItems(effectiveComponents, originalIndices);

  // 페이지네이션 로딩 시 스켈레톤 행 추가 (평탄화 여부 무관)
  // 열 타입은 호출부가 meta API로부터 전달 (iOS/Android와 동일 스펙, 미지정 시 twoCol fallback)
  const SKELETON_ROW_COUNT = 2;
  const displayItems = useMemo<FlattenedItem[]>(() => {
    if (!isFetching) return flattenedItems;

    const is3col = skeletonType === 'threeCol';

    // 3col이 아니면 2col을 기본값으로
    const skeletons: FlattenedItem[] = Array.from(
      { length: SKELETON_ROW_COUNT },
      (_, i) =>
        is3col
          ? { kind: 'skeleton-3col' as const, key: `skeleton-${i}` }
          : { kind: 'skeleton-2col' as const, key: `skeleton-${i}` },
    );

    return [...flattenedItems, ...skeletons];
  }, [flattenedItems, isFetching, skeletonType]);

  // 평탄화된 상품 행의 좋아요/프레스 핸들러
  const { navigator } = useNavigator();
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const firstFlattenTarget = useMemo(() => {
    const target = effectiveComponents.find(
      c => c.type.itemList && FLATTEN_ITEM_TYPES.has(c.type.itemList),
    );
    return target?.entity.itemList?.[0];
  }, [effectiveComponents]);

  const screenName = useMemo(() => {
    return (
      screen ??
      (firstFlattenTarget?.logging?.analytics?.SCREEN as string | undefined) ??
      ''
    );
  }, [firstFlattenTarget, screen]);

  const { addLikeGoods } = useAddLikeGoods({
    listParams: firstFlattenTarget?.logging?.analytics?.LIST_ID as
      | string
      | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: screenName,
  });

  // 평탄화된 상품의 likeMap 초기화
  useEffect(() => {
    for (const component of effectiveComponents) {
      if (
        !component.type.itemList ||
        !FLATTEN_ITEM_TYPES.has(component.type.itemList)
      )
        continue;
      const entity = component.entity.itemList;
      if (!entity) continue;
      setLikeMap(prev => ({
        ...prev,
        ...entity.reduce<Record<string, boolean>>(
          (
            acc,
            cur: { item?: { sno?: number; like?: { is_liked?: boolean } } },
          ) => {
            if (cur.item?.sno != null) {
              acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
            }
            return acc;
          },
          {},
        ),
      }));
    }
  }, [effectiveComponents, setLikeMap]);

  const handlePressGoodsItem = useCallback(
    (item: RowItem) => {
      // 일반/비디오 상품 카드 (통합)
      if (item.type === 'goods' || item.type === 'videoGoods') {
        const isVideo = item.type === 'videoGoods';
        const sno = item.sno;
        const ad = isVideo ? item.item.ad : item.ad;
        const firstPageRendering = isVideo
          ? item.item.first_page_rendering
          : item.firstPageRendering;
        const remoteDeeplink = isVideo ? undefined : item.remoteDeeplink;

        if (item.logging) {
          bridge.logAnalyticsEvent({
            name: 'CLICK_GOODS',
            analytics: {
              // fallback: 서버값이 없을 때만 사용
              ...(isNotNil(screen) && { SCREEN: screen }),
              ...(isVideo && { CARD_TYPE: 'VIDEO' }),
              // 서버값: fallback을 덮어씀
              ...item.logging.analytics,
            },
            ably_only_log: item.logging.ably_only_log,
          });
        }

        if (isNotNil(ad)) {
          bridge.logAdEvent({ action: AdAction.CLICK, ad });
        }

        // on_return inject (goods에만 onReturn 존재)
        if (!isVideo) {
          resetInjected();
          if (
            item.onReturn &&
            item.componentIndex != null &&
            item.entityIndex != null
          ) {
            fetchAndInject({
              action: item.onReturn,
              sectionIndex: item.componentIndex,
              itemIndex: item.entityIndex,
              itemSno: item.sno,
            });
          }
        }

        navigator.moveToGoods({
          sno,
          remoteDeeplink,
          firstPageRendering: firstPageRendering ?? null,
          loggingAnalytics: item.logging?.analytics as
            | Record<string, unknown>
            | undefined,
        });
        return;
      }
    },
    [navigator, screen, fetchAndInject, resetInjected],
  );

  const handlePressLike = useCallback(
    async (item: RowItem) => {
      // 일반/비디오 상품 카드 (통합)
      if (item.type === 'goods' || item.type === 'videoGoods') {
        const isVideo = item.type === 'videoGoods';
        const currentLike = likeMap[item.sno] ?? false;
        const ad = isVideo ? item.item.ad : item.ad;
        const listParamsReferrer = isVideo
          ? item.item.like?.list_id
          : item.listId;

        if (item.logging) {
          bridge.logAnalyticsEvent({
            name: currentLike ? 'CLICK_UNLIKE_GOODS' : 'CLICK_LIKE_GOODS',
            analytics: {
              ...(isNotNil(screen) && { SCREEN: screen }),
              ...(isVideo && { CARD_TYPE: 'VIDEO' }),
              ...item.logging.analytics,
            },
            ably_only_log: item.logging.ably_only_log,
          });
        }

        if (isNotNil(ad)) {
          bridge.logAdEvent({
            action: currentLike ? AdAction.UNLIKE : AdAction.LIKE,
            ad,
          });
        }

        if (currentLike) {
          await mutateRemoveLikeGoods({
            sno: item.sno,
            list_params_referrer: listParamsReferrer,
          });
          bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
          setLikeMap(prev => ({ ...prev, [item.sno]: false }));
          return;
        }

        await addLikeGoods(
          item.sno,
          item.logging?.analytics,
          item.logging?.ably_only_log,
        );
        return;
      }
    },
    [likeMap, addLikeGoods, screen, mutateRemoveLikeGoods, setLikeMap],
  );

  // 피드백 모달
  const instanceId = useInstanceId();
  const setFeedbackCache = useSetAtom(feedbackCacheAtom);
  const feedbackModal = useGoodsCardFeedbackModal();
  const logClickMeatballs = useLogClickMeatballs();

  // unmount 시 자기 instanceId 항목만 제거
  useEffect(() => {
    return () => {
      setFeedbackCache(prev => {
        const prefix = `${instanceId}:`;
        const next = new Map(prev);
        for (const key of next.keys()) {
          if (key.startsWith(prefix)) next.delete(key);
        }
        return next.size === prev.size ? prev : next;
      });
    };
  }, [instanceId, setFeedbackCache]);

  const handleOpenFeedbackModal = useCallback(
    (item: RowItem) => {
      if (item.type !== 'goods' || !item.feedbackType) return;

      logClickMeatballs({ logging: item.logging });

      feedbackModal
        .show({
          goodsSno: item.sno,
          feedbackType: item.feedbackType,
          logging: item.logging,
          screen: screenName,
        })
        .then(result => {
          if (result.type === 'confirm') {
            setFeedbackCache(prev => {
              const next = new Map(prev);
              next.set(`${instanceId}:${item.sno}`, result.data);
              return next;
            });
          }
        });
    },
    [feedbackModal, logClickMeatballs, screenName, instanceId, setFeedbackCache],
  );

  const handleCancelFeedback = useCallback(
    (goodsSno: number) => {
      setFeedbackCache(prev => {
        const next = new Map(prev);
        next.delete(`${instanceId}:${goodsSno}`);
        return next;
      });
    },
    [instanceId, setFeedbackCache],
  );

  // FlatList 빌트인 viewability 대신 실측 높이 + scrollY로 50% 임계치를 직접 계산하여 viewability 판단
  const {
    visibleComponentIds,
    isAwaitingDataChange,
    scheduleCheck: scheduleViewabilityCheck,
    updateScrollY: handleScrollY,
    resetDisplayHistory,
  } = useAtomicGoodsViewability({
    displayItems,
    itemHeights,
    viewportHeightRef: layoutRef,
    logDisabled,
    screen,
    thresholdRatio: 0.5,
    isFetching,
    isPendingScroll,
  });

  // 필터 변경을 in-render에서 감지하여 자식 렌더 전에 awaiting=true를 보장.
  // useEffect로 처리하면 자식의 useEffect가 먼저 실행되어 한 render 동안
  // isAwaitingDataChange=false인 상태로 emit이 새어나간다.
  // 첫 mount 시에는 자연스럽게 layout → runCheck → emit 흐름을 타도록 skip.
  const prevFilterParamsRef = useRef(filterParams);
  const isFirstFilterParamsRef = useRef(true);
  if (
    !isFirstFilterParamsRef.current &&
    prevFilterParamsRef.current !== filterParams
  ) {
    resetDisplayHistory();
    // 필터 변경 시 inject도 명시 폐기 (iOS setComponentReactor / Android requestReset 정합).
    // data.components의 firstId가 그대로 유지되는 필터 케이스에서 inject 상태가
    // stale로 남아 잘못된 좌표 lookup 또는 DROPPED을 유발하던 문제 방지.
    resetInjected();
  }
  prevFilterParamsRef.current = filterParams;
  isFirstFilterParamsRef.current = false;

  const handleReloadComponent = useCallback(
    async (
      componentId: number,
      carouselSno?: number,
      queryParams?: Record<string, string>,
    ) => {
      try {
        const rawComponent = await getScreenComponent({
          screenName: data.screenName,
          componentId,
          prevCarousel: carouselSno,
          queryParams,
        });
        const converted = convertScreenComponent(rawComponent, data.logging);
        setComponentOverrides(prev => ({
          ...prev,
          [componentId]: converted,
        }));
      } catch {
        // 네트워크 에러 시 무시 — 기존 컴포넌트 유지
      }
    },
    [data.screenName, data.logging],
  );

  const onScrollToIndexFailed = async (info: { index: number }) => {
    await delay(300);
    listRef.current?.scrollToIndex({
      index: info.index,
      animated: false,
    });
  };

  const handleScroll = useAnimatedScrollHandler({
    onScroll: ({ contentSize, layoutMeasurement, contentOffset }) => {
      atomicRendererScrollY.value = contentOffset.y;

      if (scrollYSharedValue) {
        scrollYSharedValue.value = contentOffset.y;
      }

      if (onScrollY) {
        scheduleOnRN(onScrollY, contentOffset.y);
      }

      // 커스텀 viewability — JS thread에 실제 scroll 위치 반영 + 재평가 예약
      scheduleOnRN(handleScrollY, contentOffset.y);

      if (
        isFetching ||
        prevContentSizeHeightRef.current === contentSize.height
      ) {
        return;
      }
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < layoutMeasurement.height * 3) {
        // FlatList가 화면을 그리기전에 scroll을 하면 계속 fetchNextPage를 호출하여 이를 방지.
        // 앱에서만 해당 문제가 발생
        prevContentSizeHeightRef.current = contentSize.height;
        scheduleOnRN(debouncedFetchNextPage);
      }
    },
    onBeginDrag: ({ contentOffset }) => {
      if (onBeginDrag) {
        scheduleOnRN(onBeginDrag, contentOffset.y);
      }
    },
    onEndDrag: ({ contentOffset }) => {
      if (onEndDrag) {
        scheduleOnRN(onEndDrag, contentOffset.y);
      }
      if (onScrollEndWorklet) {
        onScrollEndWorklet(contentOffset.y);
      }
    },
    onMomentumEnd: ({ contentOffset }) => {
      if (onScrollEndWorklet) {
        onScrollEndWorklet(contentOffset.y);
      }
    },
  });

  // stickyHeaderIndices: flattenedItems 내에서 FilterList 위치 계산
  const stickyHeaderIndices = useMemo(() => {
    if (isNil(filterListIndex)) return undefined;
    const idx = flattenedItems.findIndex(
      fi => fi.kind === 'component' && fi.originalIndex === filterListIndex,
    );
    return idx >= 0 ? [idx] : undefined;
  }, [filterListIndex, flattenedItems]);

  // FlatList CellRenderer는 [item, extraData]만 비교해 부모 state 변경 시
  // 자식 props가 stale 상태로 남는다(예: isAwaitingDataChange가 carousel까지
  // 전파되지 않음). cell이 reference하는 부모 state를 모두 모아 cell 갱신을 보장.
  const cellExtraData = useMemo(
    () => ({
      isAwaitingDataChange,
      isPendingScroll,
      visibleComponentIds,
      likeMap,
      bookmarkMap,
    }),
    [
      isAwaitingDataChange,
      isPendingScroll,
      visibleComponentIds,
      likeMap,
      bookmarkMap,
    ],
  );

  const onRefresh = () => {
    // iOS `MetaFeedCollectionVC.refreshControl` / Android `ComponentListViewModel.swipeRefresh` 와 동일.
    // useLogAnalyticsEvent로 AnalyticsContext의 SCREEN 자동 주입을 받음.
    logAnalyticsEvent({
      name: 'REFRESH_SCREEN',
      analytics: { ACTION: 'PULL' },
    });
    setIsRefreshing(true);
    resetDisplayHistory();
    // TODO: refetch를 할 때 pagination한만큼의 api를 모두 호출하는 이슈가 있음
    refetch();
    setIsRefreshing(false);
  };

  const contentSizeHeightRef = useRef<number>(0);

  const fetchNextPageIfContentSmall = () => {
    if (isFetching || isNil(data.nextToken)) return;
    if (
      contentSizeHeightRef.current > 0 &&
      layoutRef.current.height > 0 &&
      contentSizeHeightRef.current < layoutRef.current.height
    ) {
      debouncedFetchNextPage();
    }
  };

  const onLayout = (e: LayoutChangeEvent) => {
    layoutRef.current.height = e.nativeEvent.layout.height;
    fetchNextPageIfContentSmall();
    // viewport 높이가 확정/변경되면 viewability 재평가
    scheduleViewabilityCheck();
  };

  const onContentSizeChange = (_width: number, height: number) => {
    contentSizeHeightRef.current = height;
    fetchNextPageIfContentSmall();
  };
  const { bottom } = useSafeArea();

  return (
    <AtomicScreenProvider value={screen}>
      <AnimatedList
        ref={listRef}
        data={displayItems}
        // FlatList CellRenderer는 [item, extraData]만 비교해 stale 상태로 남기 때문에
        // 부모에서 변하는 cell-level state는 모두 extraData에 모아야 cell이 갱신됨.
        // 새 객체 리터럴은 매 렌더 차이를 발생시키므로 useMemo로 안정화.
        extraData={cellExtraData}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: bottom }}
        scrollIndicatorInsets={bottom ? { bottom } : undefined}
        stickyHeaderIndices={stickyHeaderIndices}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        keyExtractor={item => item.key}
        renderItem={({ item, index }) => {
          // 모든 kind를 Flex로 감싸 실측 → 커스텀 viewability의 offset 계산에 사용.
          // 이전에는 'component' kind만 측정되어 rows는 offset 계산에서 빠졌음.
          const handleItemLayout = (e: LayoutChangeEvent) => {
            const newHeight = e.nativeEvent.layout.height;
            const prevHeight = itemHeights.current[index];
            if (prevHeight === newHeight) return;
            itemHeights.current[index] = newHeight;
            // 외부 컴포넌트(HomeList 등)가 component 높이만 추적하던 계약 유지
            if (item.kind === 'component') {
              onItemHeightChange?.(index);
            }
            // 새 측정이 도착하면 viewability 재평가 — PROXY → GOODS_CAROUSEL
            // 교체나 비동기 이미지 로드로 높이가 바뀌어도 바로 반영됨.
            scheduleViewabilityCheck();
          };

          if (item.kind === 'skeleton-3col') {
            return (
              <Flex onLayout={handleItemLayout}>
                <GoodsCardSkeletonList
                  count={3}
                  numColumns={3}
                  ratio={1.2}
                  bottomContentHeight={60}
                />
              </Flex>
            );
          }
          if (item.kind === 'skeleton-2col') {
            return (
              <Flex onLayout={handleItemLayout}>
                <GoodsCardSkeletonList
                  count={2}
                  numColumns={2}
                  ratio={1.2}
                  bottomContentHeight={60}
                />
              </Flex>
            );
          }
          if (item.kind === 'row-3col') {
            return (
              <Flex onLayout={handleItemLayout}>
                <ThreeColGoodsRow
                  items={item.items}
                  onPressItem={handlePressGoodsItem}
                  onPressLike={handlePressLike}
                  likeMap={likeMap}
                  onPressMore={handleOpenFeedbackModal}
                  onLongPress={handleOpenFeedbackModal}
                  onCancelFeedback={handleCancelFeedback}
                />
              </Flex>
            );
          }
          if (item.kind === 'row-2col') {
            return (
              <Flex onLayout={handleItemLayout}>
                <TwoColGoodsRow
                  items={item.items}
                  onPressItem={handlePressGoodsItem}
                  onPressLike={handlePressLike}
                  likeMap={likeMap}
                  onPressMore={handleOpenFeedbackModal}
                  onLongPress={handleOpenFeedbackModal}
                  onCancelFeedback={handleCancelFeedback}
                />
              </Flex>
            );
          }
          // 평탄화 대상 컴포넌트의 분리된 헤더
          if (item.kind === 'header') {
            const headerType = item.component.type.header;
            const HeaderRenderer = headerType
              ? HeaderComponent[headerType]
              : null;
            if (!HeaderRenderer) return null;
            const onReloadHeader =
              headerType === AtomicHeaderType.HeadlineReloadOneLine
                ? () =>
                    handleReloadComponent(
                      item.component.componentId,
                      item.component.entity.header?.item.carousel_sno,
                    )
                : undefined;
            return (
              <Flex
                onLayout={e => {
                  const newHeight = e.nativeEvent.layout.height;
                  const prevHeight = itemHeights.current[index];
                  itemHeights.current[index] = newHeight;
                  if (isNil(prevHeight) || prevHeight !== newHeight) {
                    onItemHeightChange?.(index);
                  }
                }}
              >
                <HeaderRenderer
                  screen={screen}
                  header={item.component.entity.header}
                  onReload={onReloadHeader}
                />
              </Flex>
            );
          }
          // 평탄화 대상 컴포넌트의 분리된 푸터
          if (item.kind === 'footer') {
            const footerType = item.component.type.footer;
            const FooterRenderer = footerType
              ? FooterComponent[footerType]
              : null;
            if (!FooterRenderer) return null;
            return (
              <Flex
                onLayout={e => {
                  const newHeight = e.nativeEvent.layout.height;
                  const prevHeight = itemHeights.current[index];
                  itemHeights.current[index] = newHeight;
                  if (isNil(prevHeight) || prevHeight !== newHeight) {
                    onItemHeightChange?.(index);
                  }
                }}
              >
                <FooterRenderer
                  screen={screen}
                  footer={item.component.entity.footer}
                />
              </Flex>
            );
          }

          const comp = item.component;
          const onReload =
            comp.type.header === AtomicHeaderType.HeadlineReloadOneLine
              ? () =>
                  handleReloadComponent(
                    comp.componentId,
                    comp.entity.header?.item.carousel_sno,
                  )
              : undefined;

          return (
            <Flex onLayout={handleItemLayout}>
              <AtomicComponent
                component={comp}
                index={item.originalIndex}
                isVisible={visibleComponentIds.has(comp.componentId)}
                likeMap={likeMap}
                setLikeMap={setLikeMap}
                bookmarkMap={bookmarkMap}
                setBookmarkMap={setBookmarkMap}
                scrollToFilterList={scrollToFilterList}
                screen={screen}
                screenName={data.screenName}
                screenLogging={data.logging}
                onReload={onReload}
                onReloadWithParams={(queryParams: Record<string, string>) =>
                  handleReloadComponent(
                    comp.componentId,
                    undefined,
                    queryParams,
                  )
                }
                logDisabled={logDisabled}
                onReturnAction={fetchAndInject}
                resetInjected={resetInjected}
                componentIndex={item.originalIndex}
                isPendingScroll={isPendingScroll}
                isAwaitingDataChange={isAwaitingDataChange}
              />
            </Flex>
          );
        }}
        refreshing={isRefreshing}
        onRefresh={onRefresh}
        disableVirtualization={isWeb}
        onScrollToIndexFailed={onScrollToIndexFailed}
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
      />
      {!hideScrollToTop && (
        <ScrollToTopButton
          atomicRendererScrollY={atomicRendererScrollY}
          listRef={listRef}
        />
      )}
    </AtomicScreenProvider>
  );
};
